import React from 'react';
import { Control, useWatch } from 'react-hook-form';
import {
  Box,
  Stack,
  Typography,
  Unstable_Grid2 as Grid,
  useTheme,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { FormattedMessage, useIntl } from 'react-intl';

import { getMaterialColor } from '../../helpers';
import { useVocabulary } from '../../i18n';
import { toPoint } from '../AmcrPasDialog';
import { ColorDot } from '../design-system';
import { PreviewMap } from '../PreviewMap';
import type { FindPasFormValues } from './FindPasEditForm';
import {
  getMissingPasFields,
  getPasFindTitle,
  pasFieldLabels,
} from './pasFind';

interface FindPasSummaryProps {
  control: Control<FindPasFormValues>;
}

const WATCHED_FIELDS = [
  'type',
  'material',
  'era',
  'foundAt',
  'localization',
  'latitude',
  'longitude',
] as const;

const titleStyles = { alignItems: 'center' };
const warningStyles = { alignItems: 'flex-start', color: 'warning.main' };
const warningIconStyles = { fontSize: 18, mt: '1px' };
const mapStyles = {
  width: '100%',
  aspectRatio: '16 / 9',
  overflow: 'hidden',
  borderRadius: 1.5,
  bgcolor: 'sunken',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const noMapStyles = { color: 'text.secondary' };

const toCssColor = ([red, green, blue]: [number, number, number]) =>
  `rgb(${red}, ${green}, ${blue})`;

/** What the find is, which values PAS still needs, and where it was found. */
const FindPasSummary = ({ control }: FindPasSummaryProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const vocabulary = useVocabulary();
  const [
    type,
    material,
    era,
    foundAt,
    localization,
    latitude,
    longitude,
  ] = useWatch({ control, name: WATCHED_FIELDS });

  const markerColor = material
    ? toCssColor(getMaterialColor(material))
    : theme.palette.primary.main;
  const point = toPoint(latitude, longitude);
  const title = getPasFindTitle(vocabulary, {
    type: type?.id,
    material,
    era: era?.id,
  });

  const missingLabels = getMissingPasFields({
    type: type?.id,
    material,
    era: era?.id,
    foundAt: foundAt?.isValid ? foundAt.toISO() : null,
    localization,
    latitude,
    longitude,
  }).map((key) => intl.formatMessage(pasFieldLabels[key]));
  const missingValues = { fields: missingLabels.join(', ') };

  return (
    <Grid container spacing={2}>
      <Grid xs={12} sm={9}>
        <Stack gap={1}>
          <Stack direction="row" gap={1} sx={titleStyles}>
            <ColorDot color={markerColor} size={12} />
            <Typography variant="subtitle1">{title}</Typography>
          </Stack>
          {missingLabels.length > 0 && (
            <Stack direction="row" gap={0.75} sx={warningStyles}>
              <WarningAmberRoundedIcon sx={warningIconStyles} />
              <Typography variant="body2">
                <FormattedMessage
                  id="findPasEdit.missingValues"
                  defaultMessage="Missing for PAS: {fields}"
                  values={missingValues}
                />
              </Typography>
            </Stack>
          )}
        </Stack>
      </Grid>
      <Grid xs={12} sm={3}>
        <Box sx={mapStyles}>
          {point ? (
            <PreviewMap
              interactive={false}
              latitude={point.latitude}
              longitude={point.longitude}
              markerColor={markerColor}
            />
          ) : (
            <Typography variant="body2" sx={noMapStyles}>
              <FormattedMessage
                id="amcrPas.preview.noCoordinates"
                defaultMessage="No coordinates"
              />
            </Typography>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export { FindPasSummary };
